// ─────────────────────────────────────────────────────────────
// inwardPrint.config.js — Report 9: inward receipt
// Shape: statement (landscape) — two sections
//   1. Bill lines   — what to reconcile against the vendor bill
//   2. Rolls received — the physical rolls and their landed cost
// Data: GET /api/tape/inward/:id  (header + items[] + rolls[])
//
// GOODS + GST POSTS TO THE VENDOR; CHARGES DO NOT
// Freight, labour and other charges are paid to third parties on
// receipt, not to the vendor. They load onto the rolls so cost of
// goods reflects what the material cost to get onto the floor.
// The totals box keeps the two apart so the vendor-facing figure
// is never confused with the landed figure.
//
// UNITS: rolls are stored in whole YARDS; this receipt shows METERS.
// A full roll (total_yards = spec_yards) shows the PO's exact
// length_per_roll_m, so the yard rounding never leaks back out
// as 3,999.6 m. A roll whose meters vary converts and rounds to
// whole meters. Per-unit rupee figures print as whole rupees.
// ─────────────────────────────────────────────────────────────

import { SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));
const YARDS_PER_METER = 1.09361;

export const inwardPrintConfig = (inw) => {
  const rolls = inw.rolls || [];
  const items = inw.items || [];

  const freight = num(inw.freight_charges);
  const labour = num(inw.labour_charges);
  const other = num(inw.other_charges);
  const charges = freight + labour + other;
  const gst = num(inw.gst_amount);
  const grand = num(inw.grand_total);
  const goods = grand - gst - charges;

  const itemById = new Map(items.map((it) => [String(it.id), it]));

  // Meters for one roll: exact PO length when it arrived full,
  // otherwise yards converted back and rounded to whole meters.
  const rollMeters = (r) => {
    const it = itemById.get(String(r.inward_item_id));
    if (it && num(r.total_yards) === num(r.spec_yards)) return num(it.length_per_roll_m);
    return Math.round(num(r.total_yards) / YARDS_PER_METER);
  };

  // grams_per_meter lives on the rolls, not the items —
  // take it from any roll received on that line.
  const gpmFor = (itemId) => {
    const r = rolls.find((x) => String(x.inward_item_id) === String(itemId));
    return r ? num(r.grams_per_meter) : 0;
  };

  // ── Section 1: bill lines ──
  const itemRows = items.map((it) => {
        const specM = num(it.length_per_roll_m);
    // Weight and rate/kg come from the PO line, so the receipt
    // matches the order exactly. Spec weight is only a fallback.
    const poWeight = num(it.weight_per_roll_kg);
    const weight = poWeight > 0 ? poWeight : (specM * gpmFor(it.id)) / 1000;
    const ratePerKg = num(it.rate_per_kg) > 0
      ? num(it.rate_per_kg)
      : weight > 0 ? num(it.rate) / weight : 0;
    const specParts = [
      it.micron != null ? `${it.micron}µ` : null,
      it.width_mm != null ? `${num(it.width_mm)}mm` : null,
      specM ? `${FORMATTERS.int(specM)} m` : null,
    ].filter(Boolean);

    return {
      jumbo: it.tape_type_name || '—',
      spec: specParts.length ? specParts.join(' · ') : '—',
      quantity: it.quantity,
      rate_per_kg: ratePerKg,
           roll_weight: weight > 0 ? `${weight.toLocaleString('en-PK', { maximumFractionDigits: 3 })} kg` : '—',
      amount: it.amount,
      gst_amount: it.gst_amount,
      line_total: num(it.amount) + num(it.gst_amount),
    };
  });

  const itemQty = items.reduce((s, it) => s + num(it.quantity), 0);
  const itemAmount = items.reduce((s, it) => s + num(it.amount), 0);
  const itemGst = items.reduce((s, it) => s + num(it.gst_amount), 0);

  // ── Section 2: rolls ──
  // A roll short of its spec changes what it can be expected to
  // yield, so it is flagged here rather than discovered later.
  const rollRows = rolls.map((r) => {
    const short = num(r.total_yards) < num(r.spec_yards);
    const m = rollMeters(r);
    return {
      roll: r.vendor_roll_number,
      jumbo: short ? `${r.jumbo_name}  (SHORT)` : r.jumbo_name,
      total_meters: m,
      base_cost: r.base_cost,
      gst_alloc: r.gst_alloc,
      charge_alloc: r.charge_alloc,
      landed_cost: r.landed_cost,
      cost_per_meter: m > 0 ? num(r.landed_cost) / m : 0,
      __tone: short ? 'expense' : null,
    };
  });

  const totalMeters = rolls.reduce((s, r) => s + rollMeters(r), 0);
  const totalBase = rolls.reduce((s, r) => s + num(r.base_cost), 0);
  const totalGstAlloc = rolls.reduce((s, r) => s + num(r.gst_alloc), 0);
  const totalChargeAlloc = rolls.reduce((s, r) => s + num(r.charge_alloc), 0);
  const totalLanded = rolls.reduce((s, r) => s + num(r.landed_cost), 0);
  const avgCpm = totalMeters > 0 ? totalLanded / totalMeters : 0;

  // ── Columns + foot built together, so hiding GST keeps them aligned ──
  const billCols = [
    { col: { key: 'jumbo', label: 'Jumbo', align: 'left', fmt: 'text' }, foot: 'TOTAL' },
    { col: { key: 'spec', label: 'Specification', align: 'left', fmt: 'text', width: 48 }, foot: '' },
    { col: { key: 'quantity', label: 'Rolls', align: 'right', fmt: 'qty', width: 18 }, foot: FORMATTERS.qty(itemQty) },
    { col: { key: 'rate_per_kg', label: 'Rate / Kg', align: 'right', fmt: 'money', width: 26 }, foot: '' },
    { col: { key: 'roll_weight', label: 'Roll Weight', align: 'right', fmt: 'text', width: 26 }, foot: '' },
    { col: { key: 'amount', label: 'Amount', align: 'right', fmt: 'money', width: 28 }, foot: FORMATTERS.money(itemAmount) },
    itemGst !== 0 && { col: { key: 'gst_amount', label: 'GST', align: 'right', fmt: 'money', width: 24 }, foot: FORMATTERS.money(itemGst) },
    { col: { key: 'line_total', label: 'Total Amount', align: 'right', fmt: 'money', width: 30, bold: true }, foot: FORMATTERS.money(itemAmount + itemGst) },
  ].filter(Boolean);

  const rollCols = [
    { col: { key: 'roll', label: 'Roll #', align: 'left', fmt: 'text', width: 28 }, foot: `${rolls.length} ROLL${rolls.length === 1 ? '' : 'S'}` },
    { col: { key: 'jumbo', label: 'Jumbo', align: 'left', fmt: 'text' }, foot: '' },
    { col: { key: 'total_meters', label: 'Meters', align: 'right', fmt: 'meters', width: 26 }, foot: FORMATTERS.meters(totalMeters) },
    { col: { key: 'base_cost', label: 'Base Cost', align: 'right', fmt: 'money', width: 26 }, foot: FORMATTERS.money(totalBase) },
    totalGstAlloc !== 0 && { col: { key: 'gst_alloc', label: 'GST', align: 'right', fmt: 'money', width: 22 }, foot: FORMATTERS.money(totalGstAlloc) },
    { col: { key: 'charge_alloc', label: 'Expense', align: 'right', fmt: 'money', width: 24 }, foot: FORMATTERS.money(totalChargeAlloc) },
    { col: { key: 'landed_cost', label: 'Total Cost', align: 'right', fmt: 'money', width: 28, bold: true }, foot: FORMATTERS.money(totalLanded) },
    { col: { key: 'cost_per_meter', label: 'Cost / m', align: 'right', fmt: 'money', width: 22 }, foot: FORMATTERS.money(avgCpm) },
  ].filter(Boolean);

    const totalsBox = [
    { label: 'Goods Value', value: goods, fmt: 'money' },
  ];
  if (gst) totalsBox.push({ label: 'GST', value: gst, fmt: 'money' });
  if (freight) totalsBox.push({ label: 'Freight / Builty', value: freight, fmt: 'money' });
  if (labour) totalsBox.push({ label: 'Labour', value: labour, fmt: 'money' });
  if (other) totalsBox.push({ label: 'Other Charges', value: other, fmt: 'money' });
  totalsBox.push({
        label: 'Total',
    value: grand,
    fmt: 'money',
    emphasis: true,
    tone: 'net',
  });

  // ── Boxes ──
  const vendorRows = [
    { label: 'Vendor', value: inw.vendor_name },
    { label: 'Address', value: inw.vendor_address },
    { label: 'Contact', value: inw.vendor_contact_person },
    { label: 'Phone', value: inw.vendor_phone },
  ];
  if (inw.vendor_ntn || inw.vendor_strn) {
    vendorRows.push({
      label: 'NTN / STRN',
      value: `${inw.vendor_ntn || '—'}  /  ${inw.vendor_strn || '—'}`,
    });
  }

  const receiptRows = [
    {
      label: 'Bill Number',
      value: inw.bill_number,
      label2: 'Received',
      value2: FORMATTERS.date(inw.received_date),
    },
    {
      label: 'Against PO',
      value: inw.po_number,
      label2: 'Rolls',
      value2: rolls.length,
    },
    {
      label: 'Total Meters',
      value: FORMATTERS.meters(totalMeters),
      label2: 'Avg Cost / m',
            value2: `Rs ${Math.round(avgCpm).toLocaleString('en-PK')}/meter`,
    },
    {
      label: 'Pay Terms',
      value: inw.vendor_payment_terms_days
        ? `${inw.vendor_payment_terms_days} days`
        : 'On delivery',
    },
  ];
  if (inw.notes && String(inw.notes).trim()) {
    receiptRows.push({ label: 'Notes', value: inw.notes });
  }

  return {
    kind: 'statement',
    orientation: 'l',
    title: 'Inward Receipt',
    subtitle: `Bill ${inw.bill_number || '—'}`,

    boxes: [
      { heading: 'Vendor Details', rows: vendorRows },
      { heading: 'Receipt Details', rows: receiptRows },
    ],

    sections: [
      {
        title: 'Bill Details',
        tone: 'net',
        columns: billCols.map((c) => c.col),
        rows: itemRows,
        footRow: billCols.map((c) => c.foot),
      },
      {
        title: 'Rolls Received',
        tone: 'net',
        columns: rollCols.map((c) => c.col),
        rows: rollRows,
        footRow: rollCols.map((c) => c.foot),
      },
    ],

    totals: totalsBox,

        termsHeading: 'NOTES:',
    termsBeside: true,
    terms: [
      `Goods and GST of ${FORMATTERS.money(goods + gst)} post to the vendor's account.`,
      'Freight, labour and other charges are paid separately and load onto the rolls by value.',
      'Landed cost is what each roll cost to get onto the floor; cost per meter carries downstream to every core cut from it.',
      'Rolls marked SHORT arrived below their jumbo specification.',
    ],

    signatures: ['Received By', 'Store Keeper', 'Approved By'],
  };
};

export default inwardPrintConfig;